import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { DictionaryCollectionItemResponse } from '../../../shared/api/responses/dictionary-collection-item.response';
import { map, Observable } from 'rxjs';
import { blank, Guid } from '@web/utils';
import { DictionaryDetailsResponse } from '../../../shared/api/responses/dictionary-details.response';
import { DictionaryUpdateRequest } from '../../../shared/api/requests/dictionary-update.request';
import { DictionaryCreateRequest } from '../../../shared/api/requests/dictionary-create.request';

@Injectable({
  providedIn: 'root',
})
export class AdminDictionaryProductAttributeApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
    cache = false,
  ): Observable<CollectionResponse<DictionaryCollectionItemResponse>> {
    const path = 'admin/dictionary/product-attribute';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );

    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cache)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            DictionaryCollectionItemResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsById(
    id: Guid,
    cache = false,
  ): Observable<DictionaryDetailsResponse> {
    const path = `admin/dictionary/product-attribute/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cache)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(DictionaryDetailsResponse)),
      );
  }

  public update(id: Guid, request: DictionaryUpdateRequest): Observable<void> {
    const path = `admin/dictionary/product-attribute/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .put(
        url,
        this.provider.httpHelper.requestToBody(
          request,
          DictionaryUpdateRequest,
        ),
        { context },
      )
      .pipe(blank());
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/dictionary/product-attribute/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.delete(url).pipe(blank());
  }

  public create(request: DictionaryCreateRequest): Observable<void> {
    const path = 'admin/dictionary/product-attribute';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      DictionaryCreateRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
